import { zodResolver } from "@hookform/resolvers/zod";
import { changeUserRole, deleteUser, inviteUser, updateOrganization } from "@stryker/core/api/organization";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { Profile } from "@stryker/core/types/accounts";
import type { OrganizationDetailResponse, UpdateOrganizationPayload } from "@stryker/core/types/organization";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ArrowIcon from "@/assets/icons/arrow.svg?react";
import MailIcon from "@/assets/icons/mail.svg?react";
import PeopleIcon from "@/assets/icons/people.svg?react";
import PlusIcon from "@/assets/icons/plus.svg?react";
import DeleteIcon from "@/assets/icons/trash.svg?react";
import UserEditIcon from "@/assets/icons/user-edit.svg?react";
import Expandable from "@/components/Expandable";
import FileUploader from "@/components/FileUploader";
import Avatar from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import useOrganization from "@/hooks/useOrganization";
import useScreens from "@/hooks/useScreens";
import { cn } from "@/lib/utils";
import { displayPlural } from "@/utils/format";
import { getUserName, getUserRole } from "@/utils/mappings";

const columnHelper = createColumnHelper<Profile>();

const roleOptions = [
  { title: "Admin", value: "admin" },
  { title: "Engineer", value: "engineer" },
  { title: "User", value: "user" },
];

const companyInfoScheme = z.object({
  name: z.string(),
  email: z.string().email({ message: "This is not a valid email" }).nonempty("Email is required"),
});

type CompanyInformationScheme = z.infer<typeof companyInfoScheme>;
export default function Organization() {
  const [shouldInviteUser, setShouldInviteUser] = useState(false);
  const [memberIdToRemove, setMemberIdToRemove] = useState<null | number>(null);
  const [memberIdToChange, setMemberIdToChange] = useState<number | null>(null);

  const { data } = useOrganization();

  const userToChange = data?.users.find(({ id }) => memberIdToChange == id);
  const userToRemove = data?.users.find(({ id }) => memberIdToRemove == id);
  const isTabletOrAbove = useScreens("md", "up");

  return (
    <div>
      <Typography variant="display-lg" weight="semibold" className="mb-2 border-b border-solid border-b-black pb-2">
        Company Information
      </Typography>
      <Expandable
        titleClassName="border-b border-solid border-gray-200"
        title={
          <div className="pb-5">
            <Typography variant="text-lg" className="text-gray-900" weight="semibold">
              Main Information
            </Typography>
            <Typography variant="text-small" className="text-gray-600">
              Update your photo and personal details here.
            </Typography>
          </div>
        }
      >
        <OrganizationForm organization={data} />
      </Expandable>
      <div className="mb-2 mt-6">
        <Typography variant="text-lg" weight="semibold" className="text-gray-900">
          Team Members
        </Typography>
        <div className="mb-4 flex flex-wrap items-center border-b border-solid border-gray-200 pb-5 md:flex-nowrap">
          <Typography className="text-gray-600" variant="text-small">
            Update your members.
          </Typography>
          <div className="ml-auto mr-4 flex gap-2">
            <PeopleIcon />
            <Typography variant="text-small" weight="semibold" className="text-gray-600">
              {displayPlural({ word: "member", count: data?.users.length ?? 0 })}
            </Typography>
          </div>
          <Button
            className="mt-4 w-full md:mt-0 md:w-44"
            variant="outline"
            onClick={() => {
              setShouldInviteUser(true);
            }}
          >
            <PlusIcon />
            Add members
          </Button>
        </div>
      </div>
      {isTabletOrAbove ? (
        <TeamMemberTable
          teamMembers={data?.users}
          setMemberIdToChange={setMemberIdToChange}
          setMemberIdToRemove={setMemberIdToRemove}
        />
      ) : (
        <TeamMembersList
          teamMembers={data?.users}
          setMemberIdToChange={setMemberIdToChange}
          setMemberIdToRemove={setMemberIdToRemove}
        />
      )}

      <ChangeMemberRoleModal userToChange={userToChange} setMemberIdToChange={setMemberIdToChange} />
      <RemoveUserModal userToRemove={userToRemove} setMemberIdToRemove={setMemberIdToRemove} />
      <InviteUserModal
        organizationId={data?.id}
        shouldInviteUser={shouldInviteUser}
        setShouldInviteUser={setShouldInviteUser}
      />
    </div>
  );
}

type OrganizationFormProps = {
  organization?: OrganizationDetailResponse;
};

function OrganizationForm({ organization }: OrganizationFormProps) {
  const [avatar, setAvatar] = useState<string | null | undefined>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (!avatar) {
      setAvatar(organization?.organization_logo);
    }
  }, [organization?.organization_logo]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyInformationScheme>({
    values: { name: organization?.name ?? "", email: organization?.email ?? "" },
    resolver: zodResolver(companyInfoScheme),
  });

  const { mutate: mutateUpdateOrganization, isPending: isLoadingUpdatingOrganization } = useMutation({
    mutationFn: ({
      payload,
      organizationId,
      isMultipart,
    }: {
      payload: UpdateOrganizationPayload | FormData;
      organizationId: number;
      isMultipart?: boolean;
    }) => {
      return updateOrganization({ data: payload, id: organizationId, isMultipart });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Organization] });
    },
    onError: (error) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
  });

  const onUpdateOrgInfo = (data: any) => {
    if (!organization?.id) return;
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("email", data.email);
    // @ts-expect-error
    avatar && typeof avatar.name === "string" && formData.append("organization_logo", avatar);
    mutateUpdateOrganization({ payload: formData, organizationId: organization.id, isMultipart: !!avatar });
  };

  return (
    <form
      onSubmit={handleSubmit(onUpdateOrgInfo)}
      className="flex  flex-wrap gap-2 rounded-xl border border-solid border-gray-200"
    >
      <div className="mx-6 mt-6 grid w-full grid-cols-1 gap-2 md:grid-cols-2">
        <TextInput
          error={errors.name?.message}
          label="Company Name"
          placeholder="Enter your company name"
          {...register("name")}
        />
        <TextInput
          error={errors.email?.message}
          label="Email"
          placeholder="Enter your company email"
          {...register("email")}
        />
      </div>
      <div className="m-6">
        <FileUploader
          className="w-full"
          avatarUrl={organization?.organization_logo}
          setFile={({ file }) => setAvatar(file)}
        />
      </div>
      <div className="flex flex-1 border-t border-solid border-gray-200 px-6 py-4">
        <Button disabled={isLoadingUpdatingOrganization} type="submit" className="ml-auto w-full md:w-40">
          Save changes
        </Button>
      </div>
    </form>
  );
}

type TeamMembersListProps = {
  teamMembers?: Profile[];
  setMemberIdToChange: Dispatch<SetStateAction<number | null>>;
  setMemberIdToRemove: Dispatch<SetStateAction<number | null>>;
};

function TeamMembersList({ teamMembers = [], setMemberIdToRemove, setMemberIdToChange }: TeamMembersListProps) {
  return (
    <div className="flex flex-col gap-4">
      {teamMembers.map((member) => {
        return (
          <div className="rounded-xl border border-solid border-gray-200 px-4 py-5">
            <div className="mb-4 flex justify-between">
              <div>
                <Typography className="text-gray-500">Member</Typography>
                <Typography variant="text-md" weight="medium" className="text-gray-800">
                  {member.first_name} {member.last_name}
                </Typography>
              </div>
              <UserRole role={getUserRole(member)} />
            </div>
            <div className="mb-4">
              <Typography className="text-gray-500">Email</Typography>
              <Typography variant="text-md" weight="medium" className="text-gray-800">
                {member.email}
              </Typography>
            </div>
            <div className="flex justify-between gap-3">
              <Button className="flex-1" onClick={() => setMemberIdToChange(member.id)}>
                <UserEditIcon className="stroke-white" />
                Edit Role
              </Button>
              <Button className="flex-1" variant="outline" onClick={() => setMemberIdToRemove(member.id)}>
                <DeleteIcon />
                Delete
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

type TeamMemberTableProps = {
  teamMembers?: Profile[];
  setMemberIdToChange: Dispatch<SetStateAction<number | null>>;
  setMemberIdToRemove: Dispatch<SetStateAction<number | null>>;
};

function TeamMemberTable({ setMemberIdToRemove, setMemberIdToChange, teamMembers = [] }: TeamMemberTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => ({ avatar: row.avatar, name: getUserName(row) }), {
        header: "Members",
        cell: (info) => {
          const { name, avatar } = info.getValue();
          return (
            <div className="flex items-center gap-1">
              <Avatar size={40} url={avatar ?? undefined} />
              <Typography variant="text-small" weight="medium" className="text-gray-900">
                {name}
              </Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => {
          return (
            <Typography variant="text-small" weight="medium" className="text-gray-900">
              {info.getValue()}
            </Typography>
          );
        },
      }),
      columnHelper.accessor((user) => getUserRole(user), {
        header: "Role",
        cell: (info) => {
          const role = info.getValue();
          return <UserRole role={role} />;
        },
      }),
      columnHelper.accessor("id", {
        header: "Action",
        cell: (info) => {
          const id = info.getValue();
          return (
            <div className="flex justify-center gap-2">
              <UserEditIcon
                className="cursor-pointer"
                onClick={() => {
                  setMemberIdToChange(id);
                }}
              />
              <DeleteIcon
                className="cursor-pointer"
                onClick={() => {
                  setMemberIdToRemove(id);
                }}
              />
            </div>
          );
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    columns,
    data: teamMembers,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-2">
      <table className="w-full border-collapse">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="bg-gray-50 py-4 text-left">
                  {header.isPlaceholder ? null : (
                    <Typography className="pl-2 text-left text-gray-600" variant="text-small" weight="medium">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Typography>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="border-y border-solid border-y-gray-200">
              {row.getVisibleCells().map((cell) => {
                return (
                  <td className={cn("py-4")} key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const inviteScheme = z.object({
  email: z.string().email({ message: "This is not a valid email" }).min(1, "Email is required"),
  role: z.string().min(1, "Role is required"),
});

type InviteScheme = z.infer<typeof inviteScheme>;

type InviteUserModalProps = {
  shouldInviteUser: boolean;
  setShouldInviteUser: Dispatch<SetStateAction<boolean>>;
  organizationId?: number;
};

function InviteUserModal({ shouldInviteUser, organizationId, setShouldInviteUser }: InviteUserModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const form = useForm<InviteScheme>({
    resolver: zodResolver(inviteScheme),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: inviteUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Organization] });
      setShouldInviteUser(false);
    },
    onError: (error: string) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
  });

  const handleInvite = (data: InviteScheme) => {
    if (!organizationId) return;
    mutate({ email: data.email, organization_id: organizationId, role: data.role as "admin" | "engineer" | "user" });
  };

  return (
    <Dialog open={shouldInviteUser} onOpenChange={() => setShouldInviteUser(false)}>
      <DialogTitle className="hidden">Invite a team member</DialogTitle>
      <DialogDescription className="hidden">
        Send an email invitation to add a new team member to your organization
      </DialogDescription>
      <DialogContent>
        <div className="w-[300px] md:w-[400px]">
          <div className="mb-4 flex justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
              <PeopleIcon className="stroke-success-500" />
            </div>
          </div>
          <Typography variant="text-lg" weight="semibold" className="text-gray-900">
            Invite a team member
          </Typography>
          <Typography variant="text-small" className="text-gray-600">
            Send an invite link to a member. The role can be changed later
          </Typography>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleInvite)}>
              <div className="mt-4 flex items-end gap-4">
                <TextInput
                  startAdornment={<MailIcon className="mr-2 w-6" />}
                  label="Email"
                  placeholder="Please enter user email"
                  {...form.register("email")}
                  error={form.formState.errors.email?.message}
                />
                <FormField
                  name="role"
                  control={form.control}
                  render={({ field }) => {
                    return (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>Select role</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue defaultValue={field.value} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {roleOptions.map((option) => {
                              return (
                                <SelectItem key={option.value} value={String(option.value)}>
                                  {option.title}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {form.formState.errors?.role && (
                          <Typography variant="validationError">{form.formState.errors.role.message}</Typography>
                        )}
                      </FormItem>
                    );
                  }}
                />
              </div>
              <div className="mt-8 flex justify-between gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1"
                  disabled={isPending}
                  onClick={() => setShouldInviteUser(false)}
                >
                  Cancel
                </Button>
                <Button size="lg" className="flex-1" type="submit" disabled={isPending}>
                  Submit
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type ChangeMemberRoleModalProps = {
  userToChange?: Profile | null;
  setMemberIdToChange: Dispatch<SetStateAction<number | null>>;
};

function ChangeMemberRoleModal({ userToChange, setMemberIdToChange }: ChangeMemberRoleModalProps) {
  const [selectedRole, setSelectedRole] = useState<"admin" | "engineer" | "user">("user");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (userToChange) {
      setSelectedRole(getUserRole(userToChange));
    }
  }, [userToChange]);

  const { mutate, isPending } = useMutation({
    mutationFn: changeUserRole,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Organization] });
      setMemberIdToChange(null);
    },
    onError: (error: string) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
  });

  function handleRoleChange() {
    if (!userToChange) return;
    mutate({ user_id: userToChange.id, role: selectedRole });
  }

  return (
    <Dialog open={!!userToChange} onOpenChange={() => setMemberIdToChange(null)}>
      <DialogTitle className="hidden">Change team member role</DialogTitle>
      <DialogDescription className="hidden">Modify a team member's role and access permissions</DialogDescription>
      <DialogContent>
        <div className="mb-4 flex w-[300px] justify-between md:w-[400px]">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-violet-50">
            <UserEditIcon className="stroke-violet-500" />
          </div>
        </div>
        <Typography variant="text-lg" weight="semibold" className="text-gray-900">
          Change team member role
        </Typography>
        <Typography variant="text-small" className="text-gray-600">
          Changing users's role changes their API token access
        </Typography>
        <div className="mb-8 mt-4 flex gap-3">
          <Avatar size={40} />
          <div className="flex-1">
            <Typography>{getUserName(userToChange)}</Typography>
            <div className="mt-2">
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as "admin" | "engineer" | "user")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex justify-between gap-4">
          <Button
            disabled={isPending}
            size="lg"
            variant="outline"
            className="flex-1"
            onClick={() => setMemberIdToChange(null)}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            disabled={isPending || selectedRole === getUserRole(userToChange)}
            className="flex-1"
            onClick={handleRoleChange}
          >
            Change
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type RemoveUserModalProps = {
  userToRemove?: Profile | null;
  setMemberIdToRemove: Dispatch<SetStateAction<null | number>>;
};

function RemoveUserModal({ userToRemove, setMemberIdToRemove }: RemoveUserModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: deleteUser,
    onSuccess: async () => {
      setMemberIdToRemove(null);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Organization] });
    },
    onError: (error: string) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
  });

  return (
    <Dialog open={!!userToRemove} onOpenChange={() => setMemberIdToRemove(null)}>
      <DialogTitle className="hidden">Delete member</DialogTitle>
      <DialogDescription className="hidden">
        Permanently remove this team member from your organization
      </DialogDescription>
      <DialogContent>
        <div className="mb-4 flex justify-between">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-50">
            <DeleteIcon className="stroke-error-500" />
          </div>
        </div>
        <Typography variant="text-lg" weight="semibold" className="text-gray-900">
          Delete member
        </Typography>
        <Typography variant="text-small" className="text-gray-600">
          User{" "}
          <Typography weight="semibold" component="span">
            {getUserName(userToRemove)}
          </Typography>{" "}
          will be deleted. Are you sure you want to delete this user? This action cannot be undone.
        </Typography>
        <div className="mb-12 mt-4 flex gap-3">
          <Avatar size={40} />
          <div>
            <Typography className="text-gray-700" variant="text-small" weight="semibold">
              {getUserName(userToRemove)}
            </Typography>
            <Typography className="text-gray-600" variant="text-small">
              {userToRemove?.email}
            </Typography>
          </div>
        </div>{" "}
        <div className="flex justify-between gap-4">
          <Button
            size="lg"
            disabled={isPending}
            variant="outline"
            className="flex-1"
            onClick={() => setMemberIdToRemove(null)}
          >
            Cancel
          </Button>
          <Button disabled={isPending} size="lg" onClick={() => mutate(userToRemove!.id)} className="flex-1">
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UserRole({ role }: { role: "user" | "admin" | "engineer" }) {
  return (
    <Typography
      variant="text-small"
      weight="medium"
      className={cn(
        "h-fit w-fit rounded-md px-2 py-[2px] capitalize",
        role === "user" && "bg-blue-200 text-blue-700",
        role === "admin" && "bg-violet-200 text-violet-700",
        role === "engineer" && "bg-green-200 text-green-700"
      )}
    >
      {role}
    </Typography>
  );
}
